var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ReplaySubject, Subject, firstValueFrom } from "rxjs";
import { filter, first, timeout } from "rxjs/operators";
import { Snapshot, } from "./model";
export class SnapshotService {
    /**
     * Snapshot Service constructor.
     *
     * @param commonSource source for AuviousCommon
     * @param rtcSource source for AuviousRtc
     */
    constructor(commonSource, rtcSource, createInternalSignedUrl = false) {
        this.createInternalSignedUrl = createInternalSignedUrl;
        this.snapshotEventsSubject = new Subject();
        commonSource.subscribe((common) => {
            var _a;
            this.common = common;
            this.snapshotResource = common.apiResourceFactory("rtc-api/snapshots");
            // dispose any previous subscription
            (_a = this.snapshotEventSubscription) === null || _a === void 0 ? void 0 : _a.dispose();
            this.snapshotEventSubscription = common.events.setEventHandlers({
                onMessageReceived: (ev) => {
                    this.snapshotEventsSubject.next(ev.payload);
                },
            });
        });
        rtcSource.subscribe((rtc) => {
            this.rtc = rtc;
        });
    }
    /**
     * Use this to get Snapshot events.
     * @returns snapshot events
     */
    events() {
        return this.snapshotEventsSubject.pipe(filter((ev) => [
            "SnapshotRequestedEvent",
            "SnapshotAcquiredEvent",
            "SnapshotUploadedEvent",
            "SnapshotApprovedEvent",
            "SnapshotDiscardedEvent",
            "SnapshotCameraRequestedEvent",
        ].includes(ev.type)));
    }
    requestSnapshot(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const subject = new ReplaySubject();
            const subscription = this.common.events.setEventHandlers({
                onMessageReceived: (ev) => subject.next(ev.payload),
            });
            try {
                const response = yield this.snapshotResource.create({
                    applicationId: params.applicationId,
                    interactionId: params.interactionId,
                    sessionId: params.sessionId,
                    sessionType: params.sessionType,
                    targetUserEndpointId: params.targetEndpoint.endpoint,
                    targetUserId: params.targetEndpoint.username,
                    userEndpointId: this.rtc.endpoint(),
                    snapshotType: params.snapshotType,
                }, {
                    urlPostfix: "request",
                });
                const snapshotAcquiredEvent = yield firstValueFrom(subject.pipe(first((ev) => ev.type === "SnapshotAcquiredEvent" &&
                    ev.snapshotId === response.snapshotId), timeout(params.timeoutSeconds || 10 * 1000)));
                return Snapshot.createFromAcquiredEvent(snapshotAcquiredEvent, this.signedUrlProvider());
            }
            finally {
                subscription.dispose();
                subject.complete();
            }
        });
    }
    signedUrlProvider() {
        return {
            get: (s) => __awaiter(this, void 0, void 0, function* () {
                const signedUrlResponse = yield this.snapshotResource.create({
                    snapshotId: s,
                    userEndpointId: this.rtc.endpoint(),
                    createInternalSignedUrl: this.createInternalSignedUrl,
                }, {
                    urlPostfix: "signedUrl",
                });
                return {
                    url: signedUrlResponse.url,
                    expires: new Date(Date.parse(signedUrlResponse.validUntil)),
                };
            }),
        };
    }
    uploadSnapshot(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const formData = new FormData();
            formData.append("applicationId", params.applicationId);
            formData.append("interactionId", params.interactionId);
            formData.append("targetUserId", params.targetEndpoint.username);
            formData.append("targetUserEndpointId", params.targetEndpoint.endpoint);
            formData.append("sessionId", params.sessionId);
            formData.append("sessionType", params.sessionType);
            formData.append("userEndpointId", this.rtc.endpoint());
            formData.append("snapshotType", params.snapshotType);
            formData.append("snapshotSuffix", "png");
            formData.append("snapshotMimeType", params.blob.type);
            formData.append("image", params.blob);
            if (params.relatedSnapshotId) {
                formData.append("relatedSnapshotId", params.relatedSnapshotId);
            }
            yield this.snapshotResource.create(formData, {
                urlPostfix: "upload",
                headers: {
                    "content-type": "multipart/form-data",
                },
            });
        });
    }
    acquireSnapshot(ev, blob) {
        return __awaiter(this, void 0, void 0, function* () {
            const formData = new FormData();
            formData.append("snapshotId", ev.snapshotId);
            formData.append("userEndpointId", this.rtc.endpoint());
            formData.append("snapshotType", ev.snapshotType);
            formData.append("snapshotSuffix", "png");
            formData.append("snapshotMimeType", blob.type);
            formData.append("image", blob);
            yield this.snapshotResource.create(formData, {
                urlPostfix: "acquire",
                headers: {
                    "content-type": "multipart/form-data",
                },
            });
        });
    }
    approve(snapshot, snapshotType) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.snapshotResource.create({
                snapshotId: snapshot.id,
                snapshotType,
                userEndpointId: this.rtc.endpoint(),
            }, { urlPostfix: "approve" });
        });
    }
    discard(snapshot) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.snapshotResource.create({
                snapshotId: snapshot.id,
                userEndpointId: this.rtc.endpoint(),
            }, { urlPostfix: "discard" });
        });
    }
    requestCameraSwitch(endpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.requestCameraOperation(endpoint, "CAMERA_SWITCH");
        });
    }
    requestCameraTorch(endpoint, on) {
        return __awaiter(this, void 0, void 0, function* () {
            if (on) {
                return yield this.requestCameraOperation(endpoint, "FLASH_ON");
            }
            else {
                return yield this.requestCameraOperation(endpoint, "FLASH_OFF");
            }
        });
    }
    requestCameraOperation(endpoint, cameraRequestType, timeoutSeconds = 10 * 1000) {
        return __awaiter(this, void 0, void 0, function* () {
            const subject = new ReplaySubject();
            const subscription = this.common.events.setEventHandlers({
                onMessageReceived: (ev) => subject.next(ev.payload),
            });
            try {
                const response = yield this.snapshotResource.create({
                    cameraRequestType,
                    targetUserId: endpoint.username,
                    targetUserEndpointId: endpoint.endpoint,
                    userEndpointId: this.rtc.endpoint(),
                }, {
                    urlPostfix: "cameraRequest",
                });
                const cameraRequestProcessedEvent = yield firstValueFrom(subject.pipe(first((ev) => ev.type === "SnapshotCameraRequestProcessedEvent" &&
                    ev.snapshotCameraRequestId === response), timeout(timeoutSeconds)));
                if (!cameraRequestProcessedEvent.succeeded) {
                    throw new Error(cameraRequestProcessedEvent.additionalInformation);
                }
            }
            finally {
                subscription.dispose();
            }
        });
    }
    cameraRequestSuccessReport(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.cameraRequestReport(ev, true);
        });
    }
    cameraRequestFailureReport(ev, additionalInformation) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.cameraRequestReport(ev, false, additionalInformation);
        });
    }
    findByInteractionId(interactionId) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = (yield this.snapshotResource.get({
                params: { interactionId },
            }));
            return result.map((value) => Snapshot.createFromSnapshotDTO(value, this.signedUrlProvider()));
        });
    }
    cameraRequestReport(ev, succeeded, additionalInformation) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.snapshotResource.create({
                snapshotCameraRequestId: ev.snapshotCameraRequestId,
                succeeded,
                userEndpointId: this.rtc.endpoint(),
                additionalInformation,
            }, {
                urlPostfix: "cameraRequestRespond",
            });
        });
    }
}
